<template>
  <div :class="['notification', notificationClass]">
    <div class="notification__content">
      <div v-if="avatar || icon" class="notification__avatar">
        <span v-if="icon" :class="['notification__icon', icon]"></span>
        <img v-else :src="avatar" :alt="'notification_' + type" />
      </div>
      <p class="notification__message">{{ message }}</p>
    </div>
    <div class="notification__caption">
      <span>{{ caption }}</span>
    </div>
    <div v-if="actions.length" class="notification-actions">
      <button
        v-for="(action, index) in actions"
        :key="index"
        :class="['notification-actions__button', action.class]"
        type="button"
        @click="handleAction(action)"
      >
        <span class="notification-actions__button--focus"></span>
        <span v-if="action.icon" :class="action.icon"></span>
        <span v-else>{{ action.label }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { onMounted, computed } from 'vue'

const props = defineProps({
  message: {
    type: String,
    default: '',
  },
  avatar: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '', // info, success, warning, error
  },
  timeout: {
    type: Number,
    default: 3000,
  },
  icon: {
    type: String,
    default: '',
  },
  caption: {
    type: String,
    default: '',
  },
  color: {
    type: String,
    default: '',
  },
  actions: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['close'])

const notificationClass = computed(() => {
  return props.color ? `bg-${props.color}` : props.type
})

function close() {
  emit('close')
}

async function handleAction(action) {
  await action.handler()
  close()
}

onMounted(() => {
  if (props.timeout > 0) {
    setTimeout(close, props.timeout)
  }
})
</script>

<style lang="scss">
.notification {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  min-width: 280px;
  max-width: 95%;
  width: 100%;
  padding: 16px;
  font-size: 16px;
  font-weight: 500;
  border-radius: var(--border-radius-lg);
  background: var(--cas-main-deep-3);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.5);
  color: var(--cas-light);
  pointer-events: all;
  margin: 10px 10px 0;
  position: relative;
  transition: transform 1s, opacity 0.5s;
  z-index: 9500;
  flex-shrink: 0;

  .message-close-btn {
    position: absolute;
    right: 16px;
    top: 16px;
    height: 24px;
    width: 24px;
    left: auto;
    opacity: 0.48;
    transition: 0.3s;
    background: none;
    padding: 0 !important;
    display: block;
    font-size: 24px;
    line-height: 1;

    @media (hover: hover) {
      &:hover {
        opacity: 1;
      }
    }

    .notification-actions__button--focus {
      display: none;
    }
  }

  @media (min-width: 576px) {
    min-width: 330px;
    max-width: 480px;
  }
  &__content {
    display: flex;
  }
  &__avatar {
    border-radius: 0;
    margin-right: 16px;
    width: 28px;
    height: 28px;
  }
  &__caption {
    width: 100%;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: var(--cas-light-700);
    margin-top: 8px;
  }
  &__message {
    font-size: 18px;
    line-height: 24px;
    font-weight: 500;
    min-height: 28px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__icon {
    font-size: 24px;
    flex: 0 0 1em;
    margin-right: 16px;
    padding-top: 10px;
  }
  &-actions {
    margin-top: 16px;
    display: flex;
    gap: 8px;
    width: 100%;
    flex-direction: row-reverse;

    &__button {
      position: relative;
      flex: 10000 1 0;
      text-transform: inherit;
      color: white;
      background: var(--cas-white-opacity--7);
      cursor: pointer;
      white-space: nowrap;
      font-size: 16px;
      line-height: 20px;
      height: 36px;
      padding: 0 16px;
      min-height: 0;
      border-radius: var(--radius-1);
      border: 0;

      &.secondary {
        color: var(--cas-status);
        background: var(--cas-secondary);
      }
      &.error {
        background: var(--cas-err);
      }

      &:hover {
        .notification-actions__button--focus {
          background: currentColor;
          opacity: 0.15;
        }
      }

      &--focus {
        position: absolute;
        top: 0;
        left: 0 /* rtl:ignore */;
        width: 100%;
        height: 100%;
        pointer-events: none;
        border-radius: inherit;
        opacity: 0;
        transition: background-color 0.3s cubic-bezier(0.25, 0.8, 0.5, 1), opacity 0.4s cubic-bezier(0.25, 0.8, 0.5, 1);
        &::before {
          background: #000;
        }
        &::after {
          background: #fff;
          content: '';
          position: absolute;
          top: 0;
          left: 0 /* rtl:ignore */;
          width: 100%;
          height: 100%;
          opacity: 0;
          border-radius: inherit;
          transition: background-color 0.3s cubic-bezier(0.25, 0.8, 0.5, 1),
            opacity 0.6s cubic-bezier(0.25, 0.8, 0.5, 1);
        }
      }
    }
  }
}
.notification.info {
  background-color: #31ccec;
}
.notification.positive {
  background-color: #21ba45;
}
.notification.warning {
  background-color: #f2c037;
  color: var(--cas-dark);
}
.notification.negative {
  background-color: #c10015;
}
.notification.ongoing {
  background-color: #616161;
}
</style>
